import React, { useState, useEffect } from "react";
import { Home, MapPin, Calendar, Eye, Trash2, IndianRupee, CreditCard, Layers, Pencil } from "lucide-react";
import { motion } from "framer-motion";
import { axiosInstancev1 } from "../../../lib/axiosInstance";
import toast from "react-hot-toast";
import { load } from "@cashfreepayments/cashfree-js";
import DurationSelectionModal from "../Subscription/DurationSelectionModal";
import ConfirmDeletionModal from "../../../components/ConfirmDeletionModal";

const PostedProperties = ({ onEdit, listingTypeFilter, plotTypeFilter }) => {
    const [listings, setListings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("active");
    const [cashfree, setCashfree] = useState(null);
    const [showPayModal, setShowPayModal] = useState(false);
    const [selectedListingForPay, setSelectedListingForPay] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [listingToDelete, setListingToDelete] = useState(null);

    useEffect(() => {
        const initCashfree = async () => {
            try {
                const mode = process.env.REACT_APP_CASHFREE_MODE || "sandbox";
                const cf = await load({ mode });
                setCashfree(cf);
            } catch (err) {
                console.error("Cashfree init error:", err);
            }
        };
        initCashfree();

        // Check for merchant_order_id in URL redirect
        const urlParams = new URLSearchParams(window.location.search);
        const merchantOrderId = urlParams.get("merchant_order_id");
        if (merchantOrderId) {
            autoVerifyPayment(merchantOrderId);
        }
    }, []);

    const autoVerifyPayment = async (merchantOrderId) => {
        const toastId = toast.loading("Verifying payment status...");
        try {
            const parts = merchantOrderId.split("_");
            const listingId = parts.length >= 2 ? parts[1] : "verify";

            const res = await axiosInstancev1.post(`/properties/${listingId}/payment/verify`, {
                merchantOrderId
            });

            if (res.data.success) {
                toast.success("Payment verified! Listing activated.", { id: toastId });
                setFilter("active");
            } else {
                toast.error(res.data.message || "Payment verification failed", { id: toastId });
            }
        } catch (error) {
            console.error("Auto verify error:", error);
            toast.error(error.response?.data?.message || "Payment verification failed", { id: toastId });
        } finally {
            const newUrl = window.location.pathname;
            window.history.replaceState({}, document.title, newUrl);
            fetchMyListings();
        }
    };

    useEffect(() => {
        fetchMyListings();
    }, [filter, listingTypeFilter, plotTypeFilter]);

    const fetchMyListings = async () => {
        try {
            setLoading(true);
            const params = {
                category: 'plot',
                listing_type: listingTypeFilter,
                plot_type: plotTypeFilter
            };
            if (filter !== "all") {
                params.status = filter;
            }
            const response = await axiosInstancev1.get("/properties/my-listings", { params });
            setListings(response.data || []);
        } catch (error) {
            console.error("Error fetching listings:", error);
            toast.error("Failed to load your listings");
        } finally {
            setLoading(false);
        }
    };

    const handlePayClick = async (listing) => {
        setSelectedListingForPay(listing);
        // Quick check if listing was already paid
        const toastId = toast.loading("Checking payment status...");
        try {
            const { data } = await axiosInstancev1.post(`/properties/${listing.id}/payment/create-order`, {
                durationMonths: 3,
                returnUrl: window.location.href
            });

            if (data.activated) {
                toast.success("Payment verified! Listing activated.", { id: toastId });
                setFilter("active");
                fetchMyListings();
                return;
            }
            toast.dismiss(toastId);
            setShowPayModal(true);
        } catch (err) {
            toast.dismiss(toastId);
            setShowPayModal(true);
        }
    };

    const handlePaymentHistory = async (plan) => {
        if (!selectedListingForPay) return;
        
        try {
            const { data } = await axiosInstancev1.post(`/properties/${selectedListingForPay.id}/payment/create-order`, {
                durationMonths: plan.months,
                returnUrl: window.location.href
            });

            if (data.activated) {
                toast.success("Payment verified! Listing activated.");
                setShowPayModal(false);
                setFilter("active");
                fetchMyListings();
                return;
            }

            if (data.paymentSessionId && cashfree) {
                cashfree.checkout({
                    paymentSessionId: data.paymentSessionId,
                    redirectTarget: "_modal",
                }).then(async (result) => {
                    if (result?.error) {
                        toast.error(result.error.message || "Payment Failed");
                    } else {
                        try {
                            const verifyRes = await axiosInstancev1.post(`/properties/${selectedListingForPay.id}/payment/verify`, {
                                merchantOrderId: data.merchantOrderId
                            });
                            if (verifyRes.data.success) {
                                toast.success("Payment Successful! Listing activated.");
                                setShowPayModal(false);
                                setFilter("active");
                                fetchMyListings();
                            }
                        } catch (vErr) {
                            console.error("Verification error:", vErr);
                        }
                    }
                });
            } else {
                toast.error("Failed to create payment session");
            }
        } catch (error) {
            console.error("Payment error:", error);
            toast.error(error.response?.data?.message || "Failed to initiate payment");
        }
    };

    const handleDeleteClick = (listingId) => {
        setListingToDelete(listingId);
        setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        if (!listingToDelete) return;

        try {
            await axiosInstancev1.delete(`/properties/${listingToDelete}`);
            toast.success("Listing deleted successfully");
            setShowDeleteModal(false);
            setListingToDelete(null);
            fetchMyListings();
        } catch (error) {
            console.error("Error deleting listing:", error);
            toast.error(error.response?.data?.message || "Failed to delete listing");
        }
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            active: { bg: "bg-green-100", text: "text-green-700", label: "Active" },
            pending_payment: { bg: "bg-yellow-100", text: "text-yellow-700", label: "Pending Payment" },
            expired: { bg: "bg-red-100", text: "text-red-700", label: "Expired" },
            draft: { bg: "bg-gray-100", text: "text-gray-700", label: "Draft" }
        };

        const config = statusConfig[status] || statusConfig.draft;

        return (
            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
                {config.label}
            </span>
        );
    };

    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        const date = new Date(dateString);
        return date.toLocaleDateString("en-IN", {
            day: "numeric",
            month: "short",
            year: "numeric"
        });
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen px-3 py-4" style={{ backgroundColor: "#ffffffff" }}>
            <div className="w-full max-w-6xl mx-auto">
                <div className="bg-white rounded-[28px] shadow-lg px-5 py-5 sm:px-7 sm:py-7 lg:p-8">
                    {/* Header */}
                    <div className="mb-6">
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">Your Listings</h2>
                        <p className="text-sm text-gray-600">Manage your plot listings</p>
                    </div>

                    {/* Filters */}
                    <div className="mb-6 flex flex-wrap gap-2">
                        {["active", "pending_payment", "expired"].map((status) => (
                            <button
                                key={status}
                                onClick={() => setFilter(status)}
                                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${filter === status
                                    ? "bg-blue-600 text-white shadow-md"
                                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                                    }`}
                            >
                                {status.charAt(0).toUpperCase() + status.slice(1).replace("_", " ")}
                            </button>
                        ))}
                    </div>

                    {/* Listings */}
                    {listings.length === 0 ? (
                        <div className="text-center py-20">
                            <Layers className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">No properties found</h3>
                            <p className="text-sm text-gray-500">
                                {`No ${filter.replace("_", " ")} plots found`}
                            </p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {listings.map((listing) => (
                                <motion.div
                                    key={listing.id}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-lg transition-all duration-300"
                                >
                                    {/* Image */}
                                    <div className="relative h-40 bg-gradient-to-br from-green-100 to-green-200">
                                        {listing.thumbnail ? (
                                            <img
                                                src={listing.thumbnail}
                                                alt={listing.title_or_type || "Property"}
                                                className="w-full h-full object-contain"
                                            />
                                        ) : (
                                            <div className="flex items-center justify-center h-full">
                                                <Layers className="w-16 h-16 text-green-300" />
                                            </div>
                                        )}
                                        <div className="absolute top-3 right-3">
                                            {getStatusBadge(listing.status)}
                                        </div>
                                    </div>

                                    {/* Content */}
                                    <div className="p-5">
                                        {/* Title */}
                                        <h3 className="text-base font-semibold text-gray-900 mb-1 line-clamp-1">
                                            {listing.property_title || listing.property_type || listing.title_or_type || "Untitled Plot"}
                                        </h3>

                                        {/* Location */}
                                        <div className="flex items-start gap-2 mb-3">
                                            <MapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                                            <p className="text-[11px] text-gray-600 line-clamp-1">
                                                {listing.locality}, {listing.city}
                                            </p>
                                        </div>

                                        {/* Details Grid */}
                                        <div className="grid grid-cols-2 gap-4 mb-4 pb-4 border-b border-gray-100">
                                            <div>
                                                <p className="text-xs text-gray-500 mb-1">Price</p>
                                                <p className="text-sm font-semibold text-gray-900 flex items-center gap-1">
                                                    <IndianRupee className="w-3 h-3" />
                                                    {listing.price
                                                        ? parseFloat(listing.price).toLocaleString("en-IN")
                                                        : "N/A"}
                                                </p>
                                            </div>
                                            <div>
                                                <p className="text-xs text-gray-500 mb-1">Type</p>
                                                <p className="text-sm font-semibold text-gray-900">
                                                    {listing.plot_type || "Plot"}
                                                </p>
                                            </div>
                                            <div>
                                                <p className="text-xs text-gray-500 mb-1">Posted</p>
                                                <p className="text-sm font-semibold text-gray-900 flex items-center gap-1">
                                                    <Calendar className="w-3 h-3" />
                                                    {formatDate(listing.created_at)}
                                                </p>
                                            </div>
                                            {listing.validity_end_date && (
                                                <div>
                                                    <p className="text-xs text-gray-500 mb-1">Valid Until</p>
                                                    <p className="text-sm font-semibold text-gray-900">
                                                        {formatDate(listing.validity_end_date)}
                                                    </p>
                                                </div>
                                            )}
                                        </div>

                                        {/* Actions */}
                                        <div className="pt-2">
                                            <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar pb-1">
                                                <button
                                                    onClick={() => window.open(`/property/${listing.id}`, "_blank")}
                                                    className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors text-[11px] font-semibold whitespace-nowrap"
                                                >
                                                    <Eye className="w-3.5 h-3.5" />
                                                    View
                                                </button>

                                                <button
                                                    onClick={() => handlePayClick(listing)}
                                                    className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-green-50 text-green-600 hover:bg-green-100 transition-colors text-[11px] font-semibold whitespace-nowrap"
                                                >
                                                    <CreditCard className="w-3.5 h-3.5" />
                                                    {listing.status === "pending_payment" ? "Pay Now" : "Renew"}
                                                </button>

                                                <button
                                                    onClick={() => onEdit(listing)}
                                                    className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-orange-50 text-orange-600 hover:bg-orange-100 transition-colors text-[11px] font-semibold whitespace-nowrap"
                                                >
                                                    <Pencil className="w-3.5 h-3.5" />
                                                    Edit
                                                </button>

                                                <button
                                                    onClick={() => handleDeleteClick(listing.id)}
                                                    className="flex items-center justify-center p-2 rounded-xl bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Payment Modal */}
            <DurationSelectionModal
                isOpen={showPayModal}
                onClose={() => setShowPayModal(false)}
                onProceed={handlePaymentHistory}
                planType="PLOT"
            />

            {/* Deletion Confirmation Modal */}
            <ConfirmDeletionModal
                isOpen={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                onConfirm={confirmDelete}
                title="Confirm Deletion"
                message="Are you sure you want to delete this plot listing? This action cannot be undone."
            />
        </div>
    );
};

export default PostedProperties;
